import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

const serviceDetails = {
  longevity: {
    title: 'Longevity Program',
    subtitle: 'Your personalized roadmap to extended healthspan',
    image: 'image.png',
    overview: 'Our flagship program combines cutting-edge biomarker analysis with personalized recommendations to optimize your longevity potential.',
    features: [
      'Complete genetic profiling and interpretation',
      'Advanced blood biomarker panel (100+ markers)',
      'Lifestyle and behavior assessment',
      'Personalized nutrition plan',
      'Exercise optimization protocol',
      'Sleep quality assessment',
      'Stress management techniques',
      'Quarterly progress reviews'
    ],
    pricing: '$2,500',
    duration: '6-month program',
    cta: 'Schedule Your Consultation'
  },
  wellness: {
    title: 'Wellness Assessment',
    subtitle: 'Understand your current health status',
    image: 'image.png',
    overview: 'Get a comprehensive snapshot of your health including biomarkers, lifestyle factors, and cellular aging assessment.',
    features: [
      'Detailed health questionnaire',
      'Physical examination',
      'Advanced blood work analysis',
      'Body composition analysis',
      'Cardiovascular fitness assessment',
      'Genetic risk factor review',
      'Lifestyle recommendations',
      'Detailed health report'
    ],
    pricing: '$1,200',
    duration: '4-week assessment',
    cta: 'Book Your Assessment'
  },
  personalized: {
    title: 'Personalized Plans',
    subtitle: 'Tailored protocols for your unique biology',
    image: 'image.png',
    overview: 'We create custom nutrition, exercise, and supplementation plans based on your individual genetic profile and health markers.',
    features: [
      'One-on-one plan creation session',
      'Custom meal planning and recipes',
      'Exercise routine design',
      'Supplement optimization',
      'Sleep hygiene protocol',
      'Digital plan tracking tools',
      'Monthly check-ins',
      'Plan adjustments as needed'
    ],
    pricing: '$1,800',
    duration: '3-month program',
    cta: 'Create Your Plan'
  },
  monitoring: {
    title: 'Continuous Monitoring',
    subtitle: 'Track your progress over time',
    image: 'image.png',
    overview: 'Stay connected with our expert team through continuous monitoring and regular assessments to ensure you stay on track.',
    features: [
      'Quarterly lab assessments',
      'Monthly video consultations',
      'Real-time data tracking',
      'Performance analytics dashboard',
      'Personalized health alerts',
      'Expert recommendations',
      'Community access',
      '24/7 support line'
    ],
    pricing: '$500/month',
    duration: 'Ongoing membership',
    cta: 'Join the Program'
  },
  concierge: {
    title: 'Concierge Membership',
    subtitle: 'Premium longevity access',
    image: 'image.png',
    overview: 'Become part of our elite community with exclusive access to our top experts, cutting-edge research, and VIP amenities.',
    features: [
      'Priority expert access',
      'Exclusive research briefings',
      'Private retreat invitations',
      'Genetic counseling sessions',
      'Personalized supplement packages',
      'Wellness technology discounts',
      'Annual comprehensive evaluation',
      'Lifetime membership benefits'
    ],
    pricing: '$10,000/year',
    duration: 'Annual membership',
    cta: 'Become a VIP Member'
  },
  retreat: {
    title: 'Wellness Retreat',
    subtitle: 'Immersive longevity experience',
    image: 'image.png',
    overview: 'Join us for an exclusive multi-day retreat combining cutting-edge testing, expert education, and luxury relaxation.',
    features: [
      '4-day immersive program',
      'Luxury accommodations',
      'Gourmet longevity cuisine',
      'Expert presentations',
      'Advanced testing suite',
      'Personalized consultations',
      'Meditation and yoga sessions',
      'Networking with longevity community'
    ],
    pricing: '$4,500',
    duration: '4 days / 3 nights',
    cta: 'Reserve Your Spot'
  },
  genetic: {
    title: 'Genetic Sequencing',
    subtitle: 'Understand your DNA for optimal health',
    image: 'image.png',
    overview: 'Comprehensive whole genome sequencing with expert interpretation of your genetic predispositions and longevity potential.',
    features: [
      'Whole genome sequencing',
      'Disease risk assessment',
      'Medication sensitivity analysis',
      'Longevity gene analysis',
      'Ancestry information',
      'Expert genetic counseling',
      'Quarterly result reviews',
      'Updated recommendations'
    ],
    pricing: '$2,000',
    duration: 'One-time assessment',
    cta: 'Get Your Genome Sequenced'
  },
  nutrition: {
    title: 'Nutrition Coaching',
    subtitle: 'Optimize your diet for longevity',
    image: 'image.png',
    overview: 'Work with certified nutritionists who specialize in longevity science to create your optimal eating strategy.',
    features: [
      'Personalized nutrition plan',
      'Weekly coaching sessions',
      'Meal planning and prep guidance',
      'Restaurant guide creation',
      'Supplement recommendations',
      'Food sensitivity testing',
      'Microbiome optimization',
      'Lifestyle integration support'
    ],
    pricing: '$150/session',
    duration: 'Flexible scheduling',
    cta: 'Schedule a Nutrition Session'
  }
};

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const service = serviceDetails[id];

  if (!service) {
    return (
      <div className="pt-32 min-h-screen flex flex-col items-center justify-center px-margin-mobile">
        <h1 className="text-display-md text-on-background mb-4">Service Not Found</h1>
        <Link to="/services" className="text-accent font-bold">
          Back to Services
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-96 flex items-center justify-center overflow-hidden">
        <img
          src={service.image}
          alt={service.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/60 to-primary/40" />

        <div className="relative z-10 text-center px-margin-mobile max-w-2xl">
          <h1 className="text-display-lg text-on-primary mb-4">{service.title}</h1>
          <p className="text-headline-md text-on-primary/90">{service.subtitle}</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-section-gap px-margin-mobile bg-background">
        <div className="max-w-container-max mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="mb-12">
                <h2 className="text-headline-lg text-on-background mb-4">Overview</h2>
                <p className="text-body-md text-on-background/80 leading-relaxed">
                  {service.overview}
                </p>
              </div>

              <div>
                <h2 className="text-headline-lg text-on-background mb-6">What's Included</h2>
                <ul className="space-y-4">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <span className="material-symbols-outlined text-accent mt-1 flex-shrink-0">
                        check_circle
                      </span>
                      <span className="text-body-md text-on-background/80">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Sidebar */}
            <div>
              <div className="sticky top-24 bg-primary-container rounded-2xl p-8 border border-outline-variant/30">
                <div className="mb-6">
                  <p className="text-label-md text-on-primary-container/70 uppercase mb-2">Investment</p>
                  <p className="text-display-sm text-accent font-bold">{service.pricing}</p>
                </div>

                <div className="mb-8 pb-8 border-b border-outline-variant/20">
                  <p className="text-label-md text-on-primary-container/70 uppercase mb-2">Program Duration</p>
                  <p className="text-body-md text-on-primary-container">{service.duration}</p>
                </div>

                <Link
                  to="/contact"
                  className="w-full block px-6 py-4 bg-accent text-on-accent text-center rounded-full font-bold text-label-lg uppercase tracking-widest hover:opacity-90 transition-opacity mb-4"
                >
                  {service.cta}
                </Link>

                <button
                  onClick={() => navigate('/services')}
                  className="w-full px-6 py-3 bg-primary-container text-on-primary-container rounded-full font-semibold border border-outline-variant hover:bg-primary-container/80 transition-colors"
                >
                  Back to Services
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services Section */}
      <section className="py-section-gap px-margin-mobile bg-primary-container">
        <div className="max-w-container-max mx-auto">
          <h2 className="text-headline-lg text-on-primary-container mb-8 text-center">
            Complementary Services
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/services/longevity"
              className="p-6 bg-primary/20 rounded-xl hover:bg-primary/30 transition-colors"
            >
              <h3 className="text-headline-md text-on-primary-container mb-2">Longevity Program</h3>
              <p className="text-body-sm text-on-primary-container/70">Comprehensive assessment and roadmap</p>
            </Link>

            <Link
              to="/services/wellness"
              className="p-6 bg-primary/20 rounded-xl hover:bg-primary/30 transition-colors"
            >
              <h3 className="text-headline-md text-on-primary-container mb-2">Wellness Assessment</h3>
              <p className="text-body-sm text-on-primary-container/70">Detailed health status evaluation</p>
            </Link>

            <Link
              to="/services/monitoring"
              className="p-6 bg-primary/20 rounded-xl hover:bg-primary/30 transition-colors"
            >
              <h3 className="text-headline-md text-on-primary-container mb-2">Continuous Monitoring</h3>
              <p className="text-body-sm text-on-primary-container/70">Track progress over time</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-primary text-on-primary py-section-gap px-margin-mobile">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-display-md mb-6">Ready to Get Started?</h2>
          <p className="text-headline-md mb-12 opacity-90">
            Schedule a free consultation with one of our longevity experts today.
          </p>

          <Link
            to="/contact"
            className="inline-block px-12 py-4 bg-accent text-on-accent rounded-full font-bold text-label-lg uppercase tracking-widest hover:opacity-90 transition-opacity"
          >
            Schedule Consultation
          </Link>
        </div>
      </section>
    </div>
  );
}
